import { useCallback, useEffect, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { Attachment } from '../../../../../../models/Attachment';
import { validateAttachments, createAttachmentFromFile } from '../../../../../../utils/attachmentValidation';
import { completeAttachmentUpload, startAttachmentUpload } from '../../../../../../services/uploadAttachment';

// headers managed by the browser, passing them explicitly causes CORS errors
const BROWSER_MANAGED_HEADERS = ['content-length', 'host'];

/**
 * Parses the signed header names from the pre-signed S3 URL
 * Only headers listed in X-Amz-SignedHeaders are part of the signature and have to be included
 */
const getSignedHeaderNames = (uploadUrl: string): Set<string> => {
  const signedHeadersParam = new URL(uploadUrl).searchParams.get('X-Amz-SignedHeaders');

  if (!signedHeadersParam) return new Set();

  return new Set(
    signedHeadersParam
      .split(';')
      .map(h => h.trim().toLowerCase())
      .filter(Boolean)
  );
};

/**
 * Returns only the headers that are signed and safe to send (i.e. not browser-managed).
 */
const createUploadHeaders = (uploadUrl: string, uploadHeaders?: Record<string, string>): HeadersInit => {
  if (!uploadHeaders) return {};

  const signedHeaders = getSignedHeaderNames(uploadUrl);

  return Object.fromEntries(
    Object.entries(uploadHeaders).filter(([name]) => {
      const normalized = name.toLowerCase();
      return signedHeaders.has(normalized) && !BROWSER_MANAGED_HEADERS.includes(normalized);
    })
  );
};

const uploadFileToPresignedUrl = async (
  uploadUrl: string,
  file: File,
  uploadHeaders?: Record<string, string>
): Promise<void> => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: createUploadHeaders(uploadUrl, uploadHeaders),
    body: file
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Attachment upload responded with status ${response.status}: ${errorBody}`);
  }
};

/** Strips the query string from a pre-signed S3 URL to get the stable S3 object URL. */
const getS3UrlFromUploadUrl = (uploadUrl: string): string => uploadUrl.split('?')[0];

const uploadSingleFile = async (file: File, contactId: string): Promise<Attachment> => {
  const baseAttachment = createAttachmentFromFile(file);
  const { fileId, uploadUrl, uploadHeaders } = await startAttachmentUpload(contactId, file);

  await uploadFileToPresignedUrl(uploadUrl, file, uploadHeaders);
  await completeAttachmentUpload(contactId, fileId);

  return { ...baseAttachment, fileId, s3Url: getS3UrlFromUploadUrl(uploadUrl) };
};

interface UseAttachmentUploadProps {
  selectedEmail: string | null;
  uploadContactId?: string | null;
  attachments: Attachment[];
  updateEmailDraft: (contactId: string, draft: { attachments?: Attachment[] }) => void;
  onUploadError?: (message: string) => void;
}

interface UseAttachmentUploadReturn {
  attachmentError: string | null;
  isUploadingAttachments: boolean;
  handleFilesSelected: (files: FileList) => Promise<void>;
  handleRemoveAttachment: (attachmentId: string) => void;
  clearAttachmentError: () => void;
}

export const useAttachmentUpload = ({
  selectedEmail,
  uploadContactId,
  attachments,
  updateEmailDraft,
  onUploadError
}: UseAttachmentUploadProps): UseAttachmentUploadReturn => {
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);

  useEffect(() => {
    setAttachmentError(null);
  }, [selectedEmail]);

  const clearAttachmentError = useCallback((): void => {
    setAttachmentError(null);
  }, []);

  const handleFilesSelected = useCallback(
    async (files: FileList): Promise<void> => {
      const { validFiles, error } = validateAttachments(files, attachments);

      setAttachmentError(error ?? null);

      if (validFiles.length === 0 || !selectedEmail || !uploadContactId) {
        return;
      }

      setIsUploadingAttachments(true);

      try {
        const results = await Promise.allSettled(validFiles.map(file => uploadSingleFile(file, uploadContactId)));

        const uploaded = results
          .filter((r): r is PromiseFulfilledResult<Attachment> => r.status === 'fulfilled')
          .map(r => r.value);

        const failed = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');

        if (uploaded.length > 0) {
          updateEmailDraft(selectedEmail, { attachments: [...attachments, ...uploaded] });
        }

        if (failed.length > 0) {
          failed.forEach(r =>
            datadogLogs.logger.error('Failed to upload attachment', { channel: 'email' }, r.reason as Error)
          );

          const errorMessage = `Failed to upload ${failed.length} attachment(s). Please try again.`;
          setAttachmentError(errorMessage);
          onUploadError?.(errorMessage);
        }
      } finally {
        setIsUploadingAttachments(false);
      }
    },
    [attachments, onUploadError, selectedEmail, updateEmailDraft, uploadContactId]
  );

  const handleRemoveAttachment = useCallback(
    (attachmentId: string): void => {
      if (selectedEmail) {
        updateEmailDraft(selectedEmail, {
          attachments: attachments.filter(a => a.id !== attachmentId)
        });
      }

      setAttachmentError(null);
    },
    [attachments, selectedEmail, updateEmailDraft]
  );

  return {
    attachmentError,
    isUploadingAttachments,
    handleFilesSelected,
    handleRemoveAttachment,
    clearAttachmentError
  };
};
